import { Avatar, Text } from "@mantine/core";
import IconHeartHandshake from "@src/assets/icons/lucide/heart-handshake.svg?react";
import IconX from "@src/assets/icons/tabler/x.svg?react";
import { ROUTES } from "@src/common/constants";
import { openInviteHistoryModal } from "@src/components/referral/referral-history-modal/referral-history-modal";
import { getPublicWebAppOrigin } from "@src/utils/app-url";
import { ReferralInvitationLink } from "./components/referral-invitation-link";
import { ReferralInviteHistorySection } from "./components/referral-invite-history-section";
import { ReferralProgressSection } from "./components/referral-progress-section";
import { useCopyInvitationLink } from "./hooks/use-copy-invitation-link";
import { useMyReferralProfile } from "./queries/get-my-referral-profile";

export function ReferralModalContent({ onClose }: { onClose: () => void }) {
	const { data: referralProfile, isPending } = useMyReferralProfile();

	const referralCode = referralProfile?.referralCode;
	const invitationLink =
		referralCode != null && referralCode !== ""
			? `${getPublicWebAppOrigin()}${ROUTES.invite}/${referralCode}`
			: null;

	const validReferrals = referralProfile?.referrals?.length ?? 0;

	const { isLinkCopied, handleCopyLink } =
		useCopyInvitationLink(invitationLink);

	return (
		<div className="relative flex flex-col gap-5 sm:gap-6 p-6">
			<button
				type="button"
				onClick={onClose}
				className="sm:hidden absolute top-3 right-3 z-20 h-8 w-8 rounded-full bg-surface-subtle backdrop-blur shadow-md hover:bg-surface-hover-subtle flex items-center justify-center"
				aria-label="Close referral popup"
			>
				<IconX className="w-4 h-4 text-text-secondary" />
			</button>

			{/* Header Section */}
			<div className="flex items-center gap-4">
				<div className="relative shrink-0">
					<div className="absolute inset-0 bg-[#EECC0C] blur-xl opacity-20 rounded-full animate-pulse" />
					<Avatar
						variant="gradient"
						gradient={{ from: "#EECC0C", to: "#f59e0b", deg: 135 }}
						radius={9999}
						className="shadow-md border-2 border-surface-default w-12 h-12 sm:w-16 sm:h-16"
					>
						<IconHeartHandshake
							className="w-6 h-6 sm:w-8 sm:h-8"
							color="white"
						/>
					</Avatar>
				</div>
				<div>
					<Text
						size="lg"
						fw={700}
						className="tracking-tight text-text-primary sm:text-xl"
					>
						Earn Your Free Months
					</Text>
					<Text
						size="xs"
						c="dimmed"
						className="max-w-60 leading-relaxed sm:text-sm sm:max-w-70"
					>
						Help a friend think better — you both get rewarded.
					</Text>
				</div>
			</div>

			<div className="h-px bg-border-subtle w-full" />

			{/* Reward Progress Section */}
			<ReferralProgressSection
				validReferrals={validReferrals}
				isPending={isPending}
			/>

			{/* Invitation Link Section */}
			{invitationLink != null && (
				<ReferralInvitationLink
					invitationLink={invitationLink}
					isLinkCopied={isLinkCopied}
					onCopyLink={handleCopyLink}
				/>
			)}

			{/* Invitation History Section */}
			<ReferralInviteHistorySection
				validReferrals={validReferrals}
				isPending={isPending}
				onOpenInviteHistory={openInviteHistoryModal}
			/>
		</div>
	);
}
